import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';
import { useBaseUrl, getJsonAuthHeaders } from '../../../utils/utils';

type QATab = "overview" | "accreditation" | "audits" | "network-trust";

interface QualityMetric { name: string; score: number; target: number; trend: string; }
interface Accreditation { name: string; status: string; issued: string; expiry: string; level: string; }
interface Audit { date: string; type: string; findings: number; status: string; score: string; }

const QualityAccreditation: React.FC = () => {
    const [activeTab, setActiveTab] = useState<QATab>('overview');
    const [metrics, setMetrics] = useState<QualityMetric[]>([]);
    const [accreditations, setAccreditations] = useState<Accreditation[]>([]);
    const [audits, setAudits] = useState<Audit[]>([]);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();

    useEffect(() => { document.title = "Uzima - Quality & Accreditation"; loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const headers = await getJsonAuthHeaders();
            const [mRes, aRes, auRes] = await Promise.allSettled([
                fetch(`${baseUrl}core/api/quality-metrics/`, { headers }),
                fetch(`${baseUrl}core/api/accreditations/`, { headers }),
                fetch(`${baseUrl}core/api/audits/`, { headers }),
            ]);
            if (mRes.status === 'fulfilled' && mRes.value.ok) { const d = await mRes.value.json(); if (d.status === 'success') setMetrics(d.data || []); }
            if (aRes.status === 'fulfilled' && aRes.value.ok) { const d = await aRes.value.json(); if (d.status === 'success') setAccreditations(d.data || []); }
            if (auRes.status === 'fulfilled' && auRes.value.ok) { const d = await auRes.value.json(); if (d.status === 'success') setAudits(d.data || []); }
        } catch (err) { console.error("Load error:", err); }
        finally { setLoading(false); }
    };

    const activeCerts = accreditations.filter(a => a.status === 'active');
    const openFindings = audits.filter(a => a.status === 'in-progress').reduce((s, a) => s + a.findings, 0);

    const tabs: Array<{ key: QATab; label: string; icon: string }> = [
        { key: "overview", label: "Quality Metrics", icon: "bx bx-bar-chart-alt-2" },
        { key: "accreditation", label: "Accreditations", icon: "bx bx-award" },
        { key: "audits", label: "Audit History", icon: "bx bx-file-find" },
        { key: "network-trust", label: "Network Trust", icon: "bx bx-shield-quarter" },
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div><h1>Quality & Accreditation</h1><p>Quality indicators, accreditations, and network trust metrics</p></div>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-shield-quarter"></i></div><div className="sc-stat-value">{metrics.length}</div><div className="sc-stat-label">Metrics</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-award"></i></div><div className="sc-stat-value">{activeCerts.length}</div><div className="sc-stat-label">Active Certs</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-error"></i></div><div className="sc-stat-value">{openFindings}</div><div className="sc-stat-label">Open Findings</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-file-find"></i></div><div className="sc-stat-value">{audits.length}</div><div className="sc-stat-label">Total Audits</div></div>
                        </div>
                        <div className="sc-tabs">{tabs.map(t => (<button key={t.key} className={`sc-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}><i className={t.icon}></i> {t.label}</button>))}</div>
                        {loading ? <div className="sc-spinner"></div> : (
                            <>
                                {activeTab === 'overview' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Quality Metrics</h3></div>
                                        <div className="sc-panel-body">{metrics.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-bar-chart-alt-2"></i><h3>No Quality Metrics</h3><p>Quality indicators will appear here once configured.</p></div>
                                        ) : (
                                            <div className="sc-list-stack">{metrics.map((m, i) => (
                                                <div className="sc-list-item" key={i} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <strong style={{ fontSize: '13px' }}>{m.name}</strong>
                                                        <div style={{ display: 'flex', gap: '12px', fontSize: '12px', alignItems: 'center' }}>
                                                            <span style={{ color: 'var(--uzima-shell-muted)' }}>Target: {m.target}</span>
                                                            <span style={{ fontWeight: 600, color: 'var(--uzima-shell-accent)' }}>{m.score}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'var(--uzima-shell-border)', borderRadius: '6px', height: '6px', marginTop: '8px' }}>
                                                        <div style={{ background: Number(m.score) >= Number(m.target) ? 'var(--uzima-shell-accent)' : '#f59e0b', borderRadius: '6px', height: '100%', width: `${Math.min(Number(m.score), 100)}%`, transition: 'width 0.3s' }}></div>
                                                    </div>
                                                </div>
                                            ))}</div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'accreditation' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Accreditations</h3></div>
                                        <div className="sc-panel-body">{accreditations.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-award"></i><h3>No Accreditations</h3><p>Your accreditations will be displayed here.</p></div>
                                        ) : (
                                            <div className="sc-grid">{accreditations.map((a, i) => (
                                                <div className="sc-card" key={i} style={{ textAlign: 'center' }}><div className="sc-card-body">
                                                    <div className="sc-stat-icon teal" style={{ margin: '0 auto 12px' }}><i className="bx bx-award"></i></div>
                                                    <div className="sc-card-title">{a.name}</div>
                                                    <span className={`sc-badge-status ${a.status === 'active' ? 'agreed' : 'pending'}`}>{a.status === 'active' ? 'Active' : 'In Progress'}</span>
                                                    <div className="sc-card-desc" style={{ marginTop: '8px' }}>{a.level}</div>
                                                    {a.status === 'active' && <div className="sc-card-meta"><span>Expires: {a.expiry}</span></div>}
                                                </div></div>
                                            ))}</div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'audits' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Audit History</h3></div>
                                        <div className="sc-panel-body">{audits.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-file-find"></i><h3>No Audits</h3><p>Audit records will appear here.</p></div>
                                        ) : (
                                            <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>Date</th><th>Audit Type</th><th>Findings</th><th>Score</th><th>Status</th></tr></thead>
                                                <tbody>{audits.map((a, i) => (<tr key={i}><td>{a.date}</td><td><strong>{a.type}</strong></td><td><span className="sc-chip soft">{a.findings} findings</span></td><td>{a.score}</td>
                                                    <td><span className={`sc-badge-status ${a.status === 'closed' ? 'agreed' : 'pending'}`}>{a.status}</span></td></tr>))}</tbody></table></div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'network-trust' && (
                                    <div className="sc-panel"><div className="sc-panel-body"><div className="sc-empty"><i className="bx bx-shield-quarter"></i><h3>Network Trust Score</h3><p>Your trust score is calculated from quality metrics, accreditation status, referral completion rates, and partner feedback. A higher score makes your facility more visible on the Uzima network.</p></div></div></div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default QualityAccreditation;
